import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { InHandDateInput } from 'src/components/Input/InHandDateInput';
import { Content } from 'src/contexts/ContentContext/Content';
import { PosFormField } from 'src/core/POS/PosFormField';
import { Stack } from 'src/core/ui';
import { AlertWithSuppressionDialog } from 'src/dialogs/AlertWithSuppressionDialog';
import { useDeliveryInfoChangeDialog } from 'src/dialogs/AlertWithSuppressionDialog/useDeliveryInfoChangeDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useGetListingAndSaleIds } from 'src/modals/common/Purchase/hooks/useGetListingAndSaleIds';
import { ContentId } from 'src/utils/constants/contentId';
import { isDatePassedHours } from 'src/utils/dateTimeUtils';
import { posChangedField } from 'src/utils/posFieldUtils';
import { PurchaseTicketsInput } from 'src/utils/purchaseUtils';

import * as styles from '../TicketGroupInputTable.css';

interface InHandDateSectionProps {
  deliveryOverrideDefaultIsLoading?: boolean;
}

export const InHandDateSection = ({
  deliveryOverrideDefaultIsLoading,
}: InHandDateSectionProps) => {
  const { getValues, setValue, watch, getFieldState, formState, clearErrors } =
    useFormContext<PurchaseTicketsInput>();

  const ticketGroup = watch(`ticketGroups.${0}`);
  const updateSalesOfListings = watch('updateSalesOfListings');

  const inHandAtField: `ticketGroups.${number}.inHandDate` = `ticketGroups.${0}.inHandDate`;
  const inHandAtError = getFieldState(inHandAtField, formState).error?.message;

  const warningDialog = useBasicDialog();
  const deliveryInfoChangeHookProps = useDeliveryInfoChangeDialog(
    () => {
      setValue('updateSalesOfListings', true);
    },
    () => {
      setValue('updateSalesOfListings', false);
    }
  );

  const { saleIds } = useGetListingAndSaleIds({
    ticketGroups: [
      {
        tickets: getValues().ticketGroups.flatMap(
          (tg) => tg.tickets?.value || []
        ),
      },
    ],
  });

  const hasAnySales = saleIds.size > 0;

  const confirmUpdateSales = useCallback(() => {
    if (hasAnySales && updateSalesOfListings == undefined) {
      if (isDatePassedHours(deliveryInfoChangeHookProps.lastTimeStamp, 1)) {
        warningDialog.launchDialog();
      } else {
        setValue('updateSalesOfListings', true);
      }
    }
  }, [
    deliveryInfoChangeHookProps.lastTimeStamp,
    hasAnySales,
    setValue,
    updateSalesOfListings,
    warningDialog,
  ]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={'s'}
      className={styles.headerCheckboxSection}
    >
      <div style={{ whiteSpace: 'nowrap' }}>
        <Content id={ContentId.InHandDate} />
      </div>
      <PosFormField errors={inHandAtError}>
        <InHandDateInput
          disabled={deliveryOverrideDefaultIsLoading}
          allowEmptySelection
          onChange={(date, daysBeforeEvent) => {
            if (date) {
              clearErrors(inHandAtField);
              const dateStr = date.toISOString();
              const { ticketGroups } = getValues();
              ticketGroups.forEach((tg, index) => {
                if (dateStr !== tg.inHandDate?.value) {
                  setValue(
                    `ticketGroups.${index}.inHandDate`,
                    posChangedField(dateStr)
                  );
                }
              });

              setValue('daysBeforeEvent', daysBeforeEvent ?? undefined);
              confirmUpdateSales();
            }
          }}
          value={ticketGroup.inHandDate?.value ?? null}
          targetDate={
            ticketGroup.event?.dates?.start
              ? new Date(ticketGroup.event?.dates?.start)
              : new Date()
          }
        />
      </PosFormField>
      <AlertWithSuppressionDialog
        headerText={<Content id={ContentId.ListingUpdateConfirmation} />}
        size="md"
        {...warningDialog.dialogProps}
        alerts={[deliveryInfoChangeHookProps]}
        onOkay={() => {
          warningDialog.closeDialog();
        }}
        okText={ContentId.Yes}
        cancelText={ContentId.No}
        onCancel={() => warningDialog.closeDialog()}
      />
    </Stack>
  );
};
